import { sortOutcomes } from "@/utilities/sharedBettingUtilities";

import type { OutcomeLayoutProps } from "./outcomeLayout";
import type {
  ProcessedOutcomes,
  ProcessedOutcome,
} from "@/hooks/firestore/betting/useEvent";

export type PlayerMarketLayoutProps = Pick<
  OutcomeLayoutProps,
  | "outcomes"
  | "competitors"
  | "eventId"
  | "eventStatus"
  | "oddsFormat"
  | "selections"
  | "toggleSelection"
  | "isLoading"
  | "mainMarket"
  | "isSuspended"
> & {
  // renderFields key to group/sort/label by
  renderFieldsKey?: string;
  // key for the active search/type filter forcing "show more" open, null when none active
  forceExpand?: string | null;
};

// fallback key for outcomes with no competitorId
export const NO_PLAYER = "__no-player__";

export type PlayerGroups = Map<string, ProcessedOutcome[]>;

// groups outcomes by competitorId, shared by PlayerMarketRows and HomeAwayColumns
export const groupByPlayer = (outcomes: ProcessedOutcome[]): PlayerGroups => {
  const grouped: PlayerGroups = new Map();

  outcomes.forEach((outcome) => {
    const playerId =
      outcome.actualCompetitorId ?? outcome.competitorId ?? NO_PLAYER;
    const group = grouped.get(playerId);
    if (group) {
      group.push(outcome);
    } else {
      grouped.set(playerId, [outcome]);
    }
  });

  return grouped;
};

// true when every outcome is cleanly _HOME or _AWAY typed (one side may be empty)
export const hasCleanHomeAwaySplit = (outcomes: ProcessedOutcomes): boolean => {
  const values = Object.values(outcomes);
  const home = values.filter((outcome) => outcome.type?.endsWith("_HOME"));
  const away = values.filter((outcome) => outcome.type?.endsWith("_AWAY"));
  return values.length > 0 && home.length + away.length === values.length;
};

// column key: renderFields[renderFieldsKey].shortAbbreviation, else market/type/id
export const outcomeSlotKey = (
  outcome: ProcessedOutcome,
  renderFieldsKey?: string,
): string =>
  (renderFieldsKey
    ? outcome.renderFields?.[renderFieldsKey]?.shortAbbreviation
    : undefined) ??
  outcome.market?.id ??
  outcome.type ??
  outcome.id;

// one outcome per distinct slot; expects an already-sorted list
export const orderedSlots = (
  sortedOutcomes: ProcessedOutcome[],
  renderFieldsKey?: string,
): ProcessedOutcome[] => {
  const seen = new Set<string>();
  const slots: ProcessedOutcome[] = [];

  sortedOutcomes.forEach((outcome) => {
    const key = outcomeSlotKey(outcome, renderFieldsKey);
    if (!seen.has(key)) {
      seen.add(key);
      slots.push(outcome);
    }
  });

  return slots;
};

// sorts by threshold (2+, 5+, 10+) per market, since a grouped card merges
// markets with different threshold schemes
export const sortPlayerOutcomes = (
  outcomes: ProcessedOutcomes,
  renderFieldsKey?: string,
  isLiveViewOnly = false,
): ProcessedOutcome[] => {
  const entries = Object.entries(outcomes);

  // only trust sortKey once every outcome has one
  if (renderFieldsKey) {
    const withSortKey = entries.map(([id, outcome]) => ({
      id,
      outcome,
      sortKey: outcome.renderFields?.[renderFieldsKey]?.sortKey,
    }));

    if (
      withSortKey.length > 0 &&
      withSortKey.every(
        (entry): entry is typeof entry & { sortKey: number } =>
          entry.sortKey != null,
      )
    ) {
      return withSortKey
        .sort((a, b) => a.sortKey - b.sortKey)
        .map(({ id, outcome }) => ({ ...outcome, id }));
    }
  }

  const byMarketType = new Map<string, ProcessedOutcomes>();

  entries.forEach(([id, outcome]) => {
    const marketType = outcome.market?.marketType ?? "";
    const group = byMarketType.get(marketType) ?? {};
    group[id] = outcome;
    byMarketType.set(marketType, group);
  });

  // Map preserves insertion order
  return [...byMarketType].flatMap(([marketType, group]) =>
    sortOutcomes(group, false, marketType, isLiveViewOnly),
  );
};
